/**
 * useTranslationStore - Store para internacionalización (i18n)
 *
 * Maneja traducciones con:
 * - Zustand para cache en memoria (acceso síncrono O(1))
 * - IndexedDB (via Storage) para persistencia de JSONs
 * - useConfigStore para preferencia de locale
 * - Fetch desde backend para descargar JSONs
 *
 * @example
 * const { t, locale, setLocale } = useTranslationStore();
 * t('common.save') // → "Guardar"
 * t('game.turn', { turn: 5 }) // → "Turno 5"
 */

import { create } from 'zustand';
import { Storage } from '@storage';
import { CONFIG } from '@utils/constants';
import { Asset } from '@rts';
import useConfigStore from '@/store/useConfigStore';

// Idiomas soportados
export const SUPPORTED_LOCALES = ['en', 'es', 'pt'];
export const DEFAULT_LOCALE = 'en';

// Detecta idioma del navegador, fallback a 'en'
function detectBrowserLocale() {
  const browserLang = navigator.language?.split('-')[0];
  return SUPPORTED_LOCALES.includes(browserLang) ? browserLang : DEFAULT_LOCALE;
}

const useTranslationStore = create((set, get) => ({
  // Estado
  locale: DEFAULT_LOCALE,
  translations: {},
  isLoaded: false,
  isLoading: false,
  error: null,

  /**
   * Traduce una key con interpolación opcional
   * @param {string} key - Key de traducción (ej: 'common.save')
   * @param {Object} params - Parámetros para interpolación (ej: { turn: 5 })
   * @returns {string} - Texto traducido o "t???key" si no existe
   */
  t: (key, params = {}) => {
    const { translations } = get();
    let text = translations[key];

    if (!text) {
      console.warn(`[i18n] Missing translation: ${key}`);
      return `t???${key}`;
    }

    // Interpolación: {name} → params.name
    if (params && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), String(value));
      });
    }

    return text;
  },

  /**
   * Carga traducciones para un locale
   * @param {string} locale - Locale a cargar (en, es, pt)
   * @param {string|null} remoteVersion - Versión del backend (de /api/versions)
   *
   * Lógica de carga:
   * 1. Si remoteVersion es conocida y coincide con cache → usar cache (sin fetch)
   * 2. Si remoteVersion es null o diferente → fetch del backend
   */
  loadTranslations: async (locale, remoteVersion = null) => {
    if (!SUPPORTED_LOCALES.includes(locale)) {
      console.error(`[i18n] Unsupported locale: ${locale}`);
      return;
    }

    // Si ya está cargado este locale, no hacer nada
    if (get().locale === locale && get().isLoaded) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // 1. Intentar cargar desde IndexedDB
      const cached = await Storage.get('translations', locale);
      const cachedVersion = cached?._version || '0.0.0';

      // 2. Si tenemos versión remota y coincide con cache, usar cache sin fetch
      if (remoteVersion && cached && cachedVersion === remoteVersion) {
        console.log(`[🌐 i18n] Cache hit: ${locale} v${cachedVersion}`);
        set({
          translations: cached,
          locale,
          isLoaded: true,
          isLoading: false,
        });
        return;
      }

      // 3. Necesitamos fetch: versión diferente, no hay cache, o no hay remoteVersion
      const translationsSource = Asset.getSource('translations');
      const url = `${CONFIG.API_URL}${translationsSource.sourceUrl}${locale}`;

      console.log(`[🌐 i18n] Fetching: ${locale} (cache: v${cachedVersion}, remote: v${remoteVersion || '?'})`);
      const response = await fetch(url);

      if (!response.ok) {
        // Si el backend falla pero hay cache, usar cache
        if (cached) {
          console.log(`[🌐 i18n] Backend failed, using cache: ${locale}`);
          set({
            translations: cached,
            locale,
            isLoaded: true,
            isLoading: false,
          });
          return;
        }
        throw new Error(`Failed to fetch translations for ${locale}: ${response.status}`);
      }

      const data = await response.json();
      const backendVersion = data._version || '1.0.0';

      // 4. Guardar en cache
      console.log(`[🌐 i18n] Loaded: ${locale} v${backendVersion} (${Object.keys(data).length} keys)`);
      await Storage.set('translations', locale, data, { version: backendVersion });

      set({
        translations: data,
        locale,
        isLoaded: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('[i18n] Failed to load translations:', error);
      set({
        error: error.message,
        isLoading: false,
      });
    }
  },

  /**
   * Cambia el idioma actual
   * Descarga el nuevo JSON si no está en cache
   */
  setLocale: async (locale) => {
    if (!SUPPORTED_LOCALES.includes(locale)) {
      console.error(`[i18n] Unsupported locale: ${locale}`);
      return;
    }

    // Cargar traducciones del nuevo locale
    await get().loadTranslations(locale);

    // Guardar en useConfigStore (persiste a IndexedDB via debouncedSave)
    useConfigStore.getState().updateSetting('locale', locale);
  },

  /**
   * Inicializa desde cache (llamado en Main.jsx al iniciar la app)
   * @param {Object|null} remoteVersions - Versiones del backend { en: "1.3.0", es: "1.3.0", pt: "1.3.0" }
   *
   * Flujo:
   * 1. Lee el locale de useConfigStore (ya cargado en Main.jsx)
   * 2. Si no es válido, detecta del navegador
   * 3. Carga las traducciones (compara versión con cache)
   */
  init: async (remoteVersions = null) => {
    // Leer locale desde useConfigStore (ya inicializado en Main.jsx)
    const savedLocale = useConfigStore.getState().settings.locale;
    const locale = SUPPORTED_LOCALES.includes(savedLocale)
      ? savedLocale
      : detectBrowserLocale();

    // Obtener versión remota para este locale
    const remoteVersion = remoteVersions?.[locale] || null;

    // Cargar traducciones con comparación de versión
    await get().loadTranslations(locale, remoteVersion);
  },

  /**
   * Limpia el cache de traducciones (útil para debugging)
   */
  clearCache: async () => {
    await Storage.delete('translations', 'en');
    await Storage.delete('translations', 'es');
    await Storage.delete('translations', 'pt');
    set({ translations: {}, isLoaded: false });
    console.log('[i18n] Cache cleared');
  },
}));

export default useTranslationStore;
